import { MessageManager } from "src/core/streaming/core/messageManager";
import {
  ErrorHandler,
  MetaHandler,
  UpdateHandler
} from "src/core/streaming/core/types";
import { BilgeDataSource } from "src/core/streaming/pooledDataSource";
import {
  AssertDeviceConnectedToCloud,
  BgpNeighborInfo,
  FabricConfig,
  FabricDevice,
  FabricTransaction,
  InterconnectTransaction,
  ManagementState,
  Node,
  NodeConfig
} from "src/gen/schema/schema/schema";
import {
  SchemaType,
  schemaTypeToJSON
} from "src/gen/schema/schema_path/schema_path";
import { ObjectFilter } from "src/gen/schema/syncfollow/syncfollow";
import type {
  MultiSiteAssert,
  MultiSiteAssertTypeKey
} from "src/pages/multisite/details/assertionUtils";
import { unreachable } from "src/utils/switch";

import { BaseDataSourceApi } from "./baseDataSourceApi";

/**
 * Currently there is only 1 global bilge
 * stream. Make sure that we only use one
 * to makes calls by using a consistent ID.
 */
export const BILGE_ID: string = "all_org";

export type { MultiSiteAssert };

export class BilgeApi extends BaseDataSourceApi<BilgeDataSource> {
  initDataSource(): BilgeDataSource {
    return new BilgeDataSource(window.cnc.bilgePool);
  }

  subscribeOrgFabric(
    onSuccess: UpdateHandler<FabricConfig[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ): () => void {
    return this.getDataSource().subscribe(
      BILGE_ID, // this id is only used to get the correct stream
      MessageManager.create({
        objectId: [BILGE_ID],
        types: [SchemaType.FabricConfigType]
      }),
      MessageManager.transformer((ds) => ds.fabricConfig),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeOrgFabricTransactions(
    onSuccess: UpdateHandler<FabricTransaction[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ): () => void {
    return this.getDataSource().subscribe(
      BILGE_ID, // this id is only used to get the correct stream
      MessageManager.create({
        objectId: [BILGE_ID],
        types: [SchemaType.FabricTransactionType]
      }),
      MessageManager.transformer((ds) => ds.fabricTransaction),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeOrgFabricDevice(
    onSuccess: UpdateHandler<FabricDevice[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ): () => void {
    return this.getDataSource().subscribe(
      BILGE_ID, // this id is only used to get the correct stream
      MessageManager.create({
        objectId: [BILGE_ID],
        types: [SchemaType.FabricDeviceType]
      }),
      MessageManager.transformer((ds) => ds.fabricDevice),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeOrgFabricBoundNodes(
    filters: Partial<ObjectFilter>[],
    onSuccess: UpdateHandler<NodeConfig[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ): () => void {
    return this.getDataSource().subscribe(
      BILGE_ID, // this id is only used to get the correct stream
      MessageManager.create({
        filters,
        objectId: [BILGE_ID],
        types: [SchemaType.NodeConfigType]
      }),
      MessageManager.transformer((ds) => ds.nodeConfig),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeBgpNeighborInfos(
    filters: Partial<ObjectFilter>[],
    onSuccess: UpdateHandler<BgpNeighborInfo[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ): () => void {
    return this.getDataSource().subscribe(
      BILGE_ID, // this id is only used to get the correct stream
      MessageManager.create({
        filters,
        objectId: [BILGE_ID],
        types: [SchemaType.BgpNeighborInfoType]
      }),
      MessageManager.transformer((ds) => ds.bgpNeighborInfo),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeOrgAllFabricNodes(
    onSuccess: UpdateHandler<Node[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ): () => void {
    return this.getDataSource().subscribe(
      BILGE_ID, // this id is only used to get the correct stream
      MessageManager.create({
        objectId: [BILGE_ID],
        types: [SchemaType.NodeType]
      }),
      MessageManager.transformer((ds) => ds.node),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeNodesCloudConnectivity(
    onSuccess: UpdateHandler<AssertDeviceConnectedToCloud[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ): () => void {
    return this.getDataSource().subscribe(
      BILGE_ID, // this id is only used to get the correct stream
      MessageManager.create({
        objectId: [BILGE_ID],
        types: [SchemaType.AssertDeviceConnectedToCloudType]
      }),
      MessageManager.transformer((ds) => ds.assertDeviceConnectedToCloud),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }
  subscribeInterconnectTransaction(
    onSuccess: UpdateHandler<InterconnectTransaction[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ): () => void {
    return this.getDataSource().subscribe(
      BILGE_ID, // this id is only used to get the correct stream
      MessageManager.create({
        objectId: [BILGE_ID],
        types: [SchemaType.InterconnectTransactionType]
      }),
      MessageManager.transformer((ds) => ds.interconnectTransaction),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeDetectedDevicesConfiguration(
    onSuccess: UpdateHandler<ManagementState[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ): () => void {
    return this.getDataSource().subscribe(
      BILGE_ID, // this id is only used to get the correct stream
      MessageManager.create({
        objectId: [BILGE_ID],
        types: [SchemaType.ManagementStateType]
      }),
      MessageManager.transformer((ds) => ds.managementState),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeAllMultiSiteAssertions(
    filters: Partial<ObjectFilter>[],
    onSuccess: UpdateHandler<MultiSiteAssert[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ): () => void {
    const transformer = MessageManager.transformer(
      (series): MultiSiteAssert[] => {
        const assertType = schemaTypeToJSON(
          series.objectType
        ) as MultiSiteAssertTypeKey;
        switch (assertType) {
          case "AssertMultisiteLinkUpType":
            return series.assertMultisiteLinkUp.length
              ? series.assertMultisiteLinkUp
              : [];
          case "AssertMultisiteLatencyStableType":
            return series.assertMultisiteLatencyStable.length
              ? series.assertMultisiteLatencyStable
              : [];
          case "AssertMultisiteExpectedMtuType":
            return series.assertMultisiteExpectedMtu.length
              ? series.assertMultisiteExpectedMtu
              : [];
          case "AssertBgpPeerEstablishedType":
            return series.assertBgpPeerEstablished.length
              ? series.assertBgpPeerEstablished
              : [];
          default:
            unreachable(assertType);
        }
        return [];
      }
    );
    return this.getDataSource().subscribe(
      BILGE_ID, // this id is only used to get the correct stream
      MessageManager.create({
        filters,
        objectId: [BILGE_ID],
        types: [
          SchemaType.AllMultisiteAssertTypes,
          SchemaType.AssertBgpPeerEstablishedType
        ]
      }),
      transformer,
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }
}
const Bilge = new BilgeApi();
export { Bilge };
